import { createFileRoute } from "@tanstack/react-router";
import { CheckCircle2, CircleAlert, LoaderCircle } from "lucide-react";
import type { ReactNode } from "react";
import { useEffect, useRef, useState } from "react";
import { useTranslations } from "use-intl";
import { AuthShell } from "@/components/auth/auth-shell";
import { BrandPanel } from "@/components/auth/brand-panel";
import { FormPanel } from "@/components/auth/form-panel";
import { FormPanelHeader } from "@/components/auth/form-panel-header";
import { FormHeading } from "@/components/auth/form-heading";
import Link from "@/lib/next-shims/router-link";
import { useSearchParams } from "@/lib/next-shims/next-navigation";
import { authService } from "@/lib/api/services/auth.service";

type VerifyStatus = "verifying" | "success" | "error" | "missing-token";

export const Route = createFileRoute("/verify-email")({
  component: VerifyEmailPage,
});

function VerifyEmailPage() {
  const t = useTranslations();

  return (
    <AuthShell>
      <BrandPanel page="verify" />
      <FormPanel>
        <FormPanelHeader
          linkOpposite={{
            href: "/signin",
            prompt: "",
            cta: t("auth.verify.back_to_signin"),
            isBack: true,
          }}
        />
        <FormHeading
          title={t("auth.verify.title")}
          description={t("auth.verify.desc")}
        />
        <VerifyEmailState />
      </FormPanel>
    </AuthShell>
  );
}

function VerifyEmailState() {
  const t = useTranslations();
  const searchParams = useSearchParams();
  const token = searchParams.get("token")?.trim() ?? "";
  const requestedRef = useRef(false);
  const [status, setStatus] = useState<VerifyStatus>(
    token ? "verifying" : "missing-token",
  );
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token || requestedRef.current) return;

    requestedRef.current = true;
    setStatus("verifying");
    setError(null);

    void authService
      .verifyEmail({ token })
      .then(() => setStatus("success"))
      .catch((err) => {
        setError(err instanceof Error ? err.message : t("auth.verify.error_desc"));
        setStatus("error");
      });
  }, [t, token]);

  if (status === "verifying") {
    return (
      <StatusPanel
        tone="neutral"
        icon={<LoaderCircle className="size-5 animate-spin" />}
        title={t("auth.verify.verifying_title")}
        description={t("auth.verify.verifying_desc")}
      />
    );
  }

  if (status === "success") {
    return (
      <StatusPanel
        tone="success"
        icon={<CheckCircle2 className="size-5" />}
        title={t("auth.verify.success_title")}
        description={t("auth.verify.success_desc")}
        actionLabel={t("auth.verify.signin_cta")}
        actionHref="/signin"
      />
    );
  }

  return (
    <StatusPanel
      tone="error"
      icon={<CircleAlert className="size-5" />}
      title={
        status === "missing-token"
          ? t("auth.verify.missing_token_title")
          : t("auth.verify.error_title")
      }
      description={
        status === "missing-token"
          ? t("auth.verify.missing_token_desc")
          : error || t("auth.verify.error_desc")
      }
      actionLabel={t("auth.verify.signin_cta")}
      actionHref="/signin"
    />
  );
}

interface StatusPanelProps {
  tone: "neutral" | "success" | "error";
  icon: ReactNode;
  title: string;
  description: string;
  actionLabel?: string;
  actionHref?: string;
}

function StatusPanel({
  tone,
  icon,
  title,
  description,
  actionLabel,
  actionHref,
}: StatusPanelProps) {
  const styles = {
    neutral: {
      box: "border-[var(--border)] bg-[var(--muted)]",
      icon: "bg-[var(--muted)] text-[var(--foreground)]",
      title: "text-[var(--foreground)]",
      body: "text-[var(--muted-foreground)]",
    },
    success: {
      box: "border-[var(--success,oklch(0.55_0.16_150))] bg-[var(--success-bg,oklch(0.95_0.05_150))]",
      icon: "bg-[var(--success,oklch(0.55_0.16_150))] text-white",
      title: "text-[var(--success,oklch(0.40_0.14_150))]",
      body: "text-[var(--success,oklch(0.45_0.14_150))]",
    },
    error: {
      box: "border-[var(--destructive)] bg-[var(--destructive-bg)]",
      icon: "bg-[var(--destructive)] text-white",
      title: "text-[var(--destructive)]",
      body: "text-[var(--destructive)]",
    },
  }[tone];

  return (
    <div className={`rounded-lg border p-4 ${styles.box}`}>
      <div className="flex gap-3">
        <div className={`mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-full ${styles.icon}`}>
          {icon}
        </div>
        <div className="min-w-0">
          <h3 className={`font-medium ${styles.title}`}>{title}</h3>
          <p className={`mt-1 text-sm leading-6 ${styles.body}`}>{description}</p>
          {actionHref && actionLabel && (
            <Link
              href={actionHref}
              className="mt-4 inline-flex h-9 items-center rounded-md bg-brand-500 px-4 text-sm font-medium text-white transition-colors hover:bg-brand-600"
            >
              {actionLabel}
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
